import { Button } from "@/components/ui/button";
import {
  formatFootprint,
  HEIGHT_LABELS,
  HEIGHT_ORDER,
  type BuilderSpec,
  type BuilderSummary,
} from "@/lib/listings/builder-state";
import { cn } from "@/lib/utils";

interface SpecControlsProps {
  summary: BuilderSummary;
  spec: BuilderSpec;
  onChange: (next: Partial<BuilderSpec>) => void;
}

function Pill({
  active,
  disabled,
  onClick,
  children,
  title,
  label,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
  label?: string;
}) {
  return (
    <Button
      type="button"
      size="compact"
      variant={active ? "selected" : "outline"}
      aria-pressed={active}
      aria-label={label}
      disabled={disabled}
      title={title}
      onClick={onClick}
      className="rounded-full tabular-nums"
    >
      {children}
    </Button>
  );
}

function Group({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      <span className="text-overline mr-1 uppercase text-fg-subtle">{label}</span>
      {children}
    </div>
  );
}

function CarriedSpecs({ spec, onChange }: Pick<SpecControlsProps, "spec" | "onChange">) {
  const entries = Object.entries(spec.specs);
  if (entries.length === 0) return null;
  return (
    <Group label="Also">
      {entries.map(([field, value]) => (
        <Pill
          key={field}
          active
          label={`Remove ${value}`}
          onClick={() => onChange({ specs: Object.fromEntries(entries.filter(([key]) => key !== field)) })}
        >
          {value} ×
        </Pill>
      ))}
    </Group>
  );
}

function HeightGroup({ summary, spec, onChange }: SpecControlsProps) {
  return (
    <Group label="Height">
      {HEIGHT_ORDER.map((bucket) => {
        const count = summary.heights.find((row) => row.bucket === bucket)?.count ?? 0;
        return (
          <Pill
            key={bucket}
            active={spec.heightBucket === bucket}
            disabled={count === 0}
            title={count === 0 ? "None listed right now" : `${count} listed`}
            onClick={() => onChange({ heightBucket: spec.heightBucket === bucket ? null : bucket })}
          >
            {HEIGHT_LABELS[bucket]}
          </Pill>
        );
      })}
      <Pill active={spec.heightBucket === null} label="Any height" onClick={() => onChange({ heightBucket: null })}>
        Any
      </Pill>
    </Group>
  );
}

export function SpecControls({ summary, spec, onChange }: SpecControlsProps) {
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-2 px-1 pb-2 pt-3">
      <Group label="Footprint">
        {summary.footprints.map((footprint) => (
          <Pill
            key={footprint.value}
            active={spec.footprint === footprint.value}
            title={`${footprint.count} listed`}
            onClick={() =>
              onChange({ footprint: spec.footprint === footprint.value ? null : footprint.value })
            }
          >
            {formatFootprint(footprint.value)}
          </Pill>
        ))}
        <Pill active={spec.footprint === null} label="Any footprint" onClick={() => onChange({ footprint: null })}>
          Any
        </Pill>
      </Group>
      <HeightGroup summary={summary} spec={spec} onChange={onChange} />
      <CarriedSpecs spec={spec} onChange={onChange} />
      {summary.walls.length > 0 ? (
        <Group label="Walls">
          <Pill active={spec.walls === null} label="Any wall count" onClick={() => onChange({ walls: null })}>
            Any
          </Pill>
          {summary.walls.map((wall) => (
            <Pill
              key={wall.value}
              active={spec.walls === wall.value}
              label={`${wall.value} walls`}
              title={`${wall.count} listed`}
              onClick={() => onChange({ walls: spec.walls === wall.value ? null : wall.value })}
            >
              {wall.value}
            </Pill>
          ))}
        </Group>
      ) : null}
    </div>
  );
}
